import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CssBaseline,
  Typography,
  Paper,
  Grid,
  Link,
  Divider,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import AppTheme from "../../../theme/AppTheme";
import { dataGridCustomizations } from "../../../theme/customizations";
import { getCampaignAnalyticsByCampaignId } from "../../../service/BuyerConfiguration/CampaignManagement/campaignService";
import { notifyError } from "../../../components/ToastContainer/Toast/Toast";
import { formatCouponTypeLabel } from "./utils/couponTypeLabel";

type CampaignAnalyticsPayload = {
  campaign?: {
    id?: number | string;
    campaign_key?: string;
    title?: string;
    status?: string;
  };
  period?: { from?: string; to?: string };
  codes?: {
    total?: number;
    active?: number;
    inactive?: number;
    expired?: number;
    revoked?: number;
  };
  funnel?: {
    reserved?: number;
    redeemed?: number;
    rolled_back?: number;
    failed?: number;
    unique_redeemed_users?: number;
    redemption_rate?: number;
  };
  financials?: {
    total_discount_amount?: number;
    avg_discount_amount?: number;
    delivery_waived_count?: number;
    orders_with_coupon?: number;
  };
  daily?: { date?: string; redeemed_count?: number; total_discount_amount?: number }[];
  top_coupons?: {
    coupon_id?: number;
    code?: string;
    type?: string;
    redeemed_count?: number;
    total_discount_amount?: number;
  }[];
};

const formatDateOnly = (dateString?: string) => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const isValidDateString = (value?: string | null) => {
  if (value === undefined || value === null) return false;
  const s = String(value).trim();
  if (!s.length) return false;
  const t = new Date(s).getTime();
  return !Number.isNaN(t);
};

const StatCard = ({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      p: 2.5,
      boxShadow: "none",
      background: "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(0,0,0,0.00) 55%)",
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {subtitle ? (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    ) : null}
  </Paper>
);

const CampaignAnalyticsDashboard = (props: { disableCustomTheme?: boolean }) => {
  const xThemeComponents = useMemo(() => ({ ...dataGridCustomizations }), []);
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CampaignAnalyticsPayload | null>(null);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  const periodDisplay = useMemo(() => {
    const apiFrom = data?.period?.from;
    const apiTo = data?.period?.to;
    if (isValidDateString(apiFrom) && isValidDateString(apiTo)) {
      return { mode: "range" as const, from: apiFrom as string, to: apiTo as string };
    }
    if (fromDate && toDate) {
      return {
        mode: "range" as const,
        from: fromDate.startOf("day").format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        to: toDate.endOf("day").format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      };
    }
    return { mode: "allTime" as const };
  }, [data?.period?.from, data?.period?.to, fromDate, toDate]);

  useEffect(() => {
    const load = async () => {
      if (!campaignId) return;
      try {
        setLoading(true);
        const res = await getCampaignAnalyticsByCampaignId(campaignId);
        const payload: CampaignAnalyticsPayload =
          (res as any)?.data?.data ?? (res as any)?.data ?? res ?? null;
        setData(payload);
      } catch (e: any) {
        notifyError(e?.response?.data?.message || "Failed to load campaign analytics");
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [campaignId]);

  const c = data?.campaign;

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Box sx={{ mr: 2, mb: 1 }}>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 800 }}>
              Campaign Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Performance insights for this campaign.
            </Typography>
            <Link
              component={RouterLink}
              to={campaignId ? `/campaign-management/${campaignId}` : "/campaign-management/list"}
              underline="hover"
              sx={{ fontWeight: 400, fontSize: 12, display: "inline-block", mt: 0.75 }}
              onClick={(e) => {
                e.preventDefault();
                navigate(campaignId ? `/campaign-management/${campaignId}` : "/campaign-management/list");
              }}
            >
              View Campaign Details
            </Link>
          </Box>
          <Box sx={{ ml: "auto", mb: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Box sx={{ mr: 1, mb: { xs: 1, sm: 0 } }}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    maxDate={dayjs()}
                    onChange={(v) => {
                      setFromDate(v);
                      if (!v) setToDate(null);
                    }}
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: { xs: "100%", sm: 160 },
                          "& .MuiInputBase-root": { height: 40 },
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ mr: 1, mb: { xs: 1, sm: 0 } }}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    minDate={dayjs()}
                    onChange={async (v) => {
                      setToDate(v);
                      if (!campaignId || !v) return;
                      try {
                        setLoading(true);
                        const fromIso = (fromDate ?? v).startOf("day").format('YYYY-MM-DDTHH:mm:ss.SSSZ');
                        const toIso = v.endOf("day").format('YYYY-MM-DDTHH:mm:ss.SSSZ');
                        const res = await getCampaignAnalyticsByCampaignId(campaignId, {
                          from: fromIso,
                          to: toIso,
                        });
                        const payload: CampaignAnalyticsPayload =
                          (res as any)?.data?.data ?? (res as any)?.data ?? res ?? null;
                        setData(payload);
                      } catch (e: any) {
                        notifyError(e?.response?.data?.message || "Failed to load campaign analytics");
                      } finally {
                        setLoading(false);
                      }
                    }}
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: { xs: "100%", sm: 160 },
                          "& .MuiInputBase-root": { height: 40 },
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ mb: { xs: 1, sm: 0 } }}>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label="Reset date filters"
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                    onClick={async () => {
                      if (!campaignId) return;
                      setFromDate(null);
                      setToDate(null);
                      try {
                        setLoading(true);
                        const res = await getCampaignAnalyticsByCampaignId(campaignId);
                        const payload: CampaignAnalyticsPayload =
                          (res as any)?.data?.data ?? (res as any)?.data ?? res ?? null;
                        setData(payload);
                      } catch (e: any) {
                        notifyError(e?.response?.data?.message || "Failed to load campaign analytics");
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </LocalizationProvider>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2.5,
            boxShadow: "none",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", alignItems: "flex-start" }}>
            <Box sx={{ mr: 2, mb: { xs: 2, sm: 0 } }}>
              {periodDisplay.mode === "allTime" ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Overall Campaign Analytics (All Time)
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Period
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.25 }}>
                    {formatDateOnly(periodDisplay.from)} — {formatDateOnly(periodDisplay.to)}
                  </Typography>
                </>
              )}
            </Box>

            <Box sx={{ ml: "auto", textAlign: "right" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "baseline" }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mr: 1 }}>
                  Campaign Name:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {c?.title || "-"}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Key: {c?.campaign_key || "-"}
              </Typography>
              {c?.status ? (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={c.status.charAt(0).toUpperCase() + c.status.slice(1).toLowerCase()}
                    size="small"
                    color={c.status.toLowerCase() === "active" ? "success" : "default"}
                    variant="outlined"
                  />
                </Box>
              ) : null}
            </Box>
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", mt: { xs: 1, sm: 0 }, width: { xs: "100%", sm: "auto" } }}>
                <CircularProgress size={18} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading…
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2.5,
            boxShadow: "none",
            mt: 2,
            mb: 2,
            backgroundColor: "#ffffff",
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 800, mb: 1 }}>
            Financials
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container sx={{ mx: { xs: 0, md: -1 } }}>
            <Grid item xs={12} md={3}>
              <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
                <StatCard title="Total discount" value={data?.financials?.total_discount_amount ?? "-"} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
                <StatCard title="Avg discount" value={data?.financials?.avg_discount_amount ?? "-"} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
                <StatCard title="Delivery waived" value={data?.financials?.delivery_waived_count ?? "-"} />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
                <StatCard title="Orders with coupon" value={data?.financials?.orders_with_coupon ?? "-"} />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
          Coupon codes
        </Typography>
        <Grid container sx={{ mx: { xs: 0, md: -1 } }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
              <StatCard title="Total" value={data?.codes?.total ?? "-"} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
              <StatCard title="Active" value={data?.codes?.active ?? "-"} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
              <StatCard title="Inactive" value={data?.codes?.inactive ?? "-"} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
              <StatCard title="Expired" value={data?.codes?.expired ?? "-"} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
              <StatCard title="Revoked" value={data?.codes?.revoked ?? "-"} />
            </Box>
          </Grid>
        </Grid>

        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, mt: 1 }}>
          Funnel
        </Typography>
        <Grid container sx={{ mx: { xs: 0, md: -1 }, mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 }, height: "100%" }}>
              <Paper
                elevation={0}
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2.5,
                  boxShadow: "none",
                  height: "100%",
                  background: "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(0,0,0,0.00) 55%)",
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Redeemed
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  {data?.funnel?.redeemed ?? "-"}
                </Typography>

                <Box sx={{ mt: 1.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    Reserved : {data?.funnel?.reserved ?? "-"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    Rollback : {data?.funnel?.rolled_back ?? "-"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    Failed : {data?.funnel?.failed ?? "-"}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 }, height: "100%" }}>
              <Paper
                elevation={0}
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2.5,
                  boxShadow: "none",
                  height: "100%",
                  background: "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(0,0,0,0.00) 55%)",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Redemption rate
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Unique Users: {data?.funnel?.unique_redeemed_users ?? "-"}
                  </Typography>
                </Box>

                {data?.funnel?.redemption_rate !== undefined &&
                data?.funnel?.redemption_rate !== null ? (
                  <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                    <Box sx={{ position: "relative", width: 120, height: 120 }}>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={120}
                        thickness={5}
                        sx={{ color: (theme) => theme.palette.action.disabledBackground }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(100, Number(data.funnel.redemption_rate)))}
                        size={120}
                        thickness={5}
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                          {`${data.funnel.redemption_rate}%`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                    -
                  </Typography>
                )}
              </Paper>
            </Box>
          </Grid>
        </Grid>

        <Grid container sx={{ mx: { xs: 0, md: -1 } }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: 2 }}>
              <Paper elevation={0} sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, p: 2, boxShadow: "none" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
                  Daily activity
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Redeemed
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Discount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.daily && data.daily.length > 0 ? (
                      data.daily.map((row, idx) => (
                        <TableRow key={`${row.date}-${idx}`}>
                          <TableCell>{row.date ? formatDateOnly(row.date) : "-"}</TableCell>
                          <TableCell align="right">{row.redeemed_count ?? "-"}</TableCell>
                          <TableCell align="right">{row.total_discount_amount ?? "-"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            No daily data
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: 2 }}>
              <Paper elevation={0} sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, p: 2, boxShadow: "none" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
                  Top coupons
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Redeemed
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Discount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.top_coupons && data.top_coupons.length > 0 ? (
                      data.top_coupons.map((row, idx) => (
                        <TableRow key={`${row.code}-${idx}`}>
                          <TableCell>{row.code || "-"}</TableCell>
                          <TableCell>{formatCouponTypeLabel(row.type)}</TableCell>
                          <TableCell align="right">{row.redeemed_count ?? "-"}</TableCell>
                          <TableCell align="right">{row.total_discount_amount ?? "-"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            No top coupons data
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </AppTheme>
  );
};

export default CampaignAnalyticsDashboard;
